// P3e — adapt an AORA production workbook (Refinery/Toledo format) into the OAS YieldData model, so
// the OAS Diagnostic tab can render a refinery-sourced dataset's BALANCE. Self-contained (mirrors the
// Refinery parser's column contract) to avoid a cross-component refactor.
//
// HONEST LIMIT: AORA carries no per-tank, per-day density or temperature, so the OAS *density &
// temperature* diagnostics have no source for such datasets — that view shows an explicit "no data"
// state rather than a fabricated number. Only the balance transfers.
import * as XLSX from 'xlsx';
import { YieldData, YieldRow } from './massBalanceOas';
import { heuristicSubCat, SUBCATS } from './massBalanceMapping';

const num = (v: any): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

// AORA "Volume" sheet columns (0-indexed): 0 product | 5 opening | 6 closing | 10 shipments |
// 11 receipts | 12 reclass | 15 production. Rows start after a row whose col 0 == "products".
export function aoraToYieldData(wb: XLSX.WorkBook): YieldData | null {
  const ws = wb.Sheets['Volume'] || wb.Sheets[wb.SheetNames[0]];
  if (!ws) return null;
  const rows = XLSX.utils.sheet_to_json<any[]>(ws, { header: 1, blankrows: false });
  let start = 0;
  for (let i = 0; i < rows.length; i++) {
    if (String(rows[i]?.[0] ?? '').trim().toLowerCase() === 'products') { start = i + 1; break; }
  }
  const out: YieldRow[] = [];
  for (let i = start; i < rows.length; i++) {
    const r = rows[i]; if (!r) continue;
    const product = String(r[0] ?? '').trim();
    if (!product || /^total/i.test(product)) continue;
    const opening = num(r[5]); const closing = num(r[6]); const ship = num(r[10]);
    const rec = num(r[11]); const recl = num(r[12]); const prod = num(r[15]);
    if (![opening, closing, ship, rec, recl, prod].some((v) => Math.abs(v) > 0.5)) continue;
    // Sign production so the balance closes (crude consumed ⇒ negative), same rule the Refinery uses.
    const base = opening + rec - ship - recl;
    const useAdd = Math.abs(base + prod - closing) <= Math.abs(base - prod - closing);
    const netProd = useAdd ? prod : -prod;
    const subId = heuristicSubCat(product) || 'byproducts-other';
    const sub = SUBCATS.find((s) => s.id === subId);
    out.push({
      sap: product, desc: product,
      opening, receipts: rec, deliveries: ship, ownCons: 0, closing,
      intakeProd: netProd, adjustments: recl, netProd,
      group: sub ? sub.label : 'Other',
    });
  }
  return out.length
    ? { rows: out, mtd: { crudeProcessed: null, totalFuel: null, unaccountedLoss: null, lossPct: null, fuelPct: null }, basis: 'volume' as const, sheet: 'refinery' }
    : null;
}
